import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";
import { richT } from "@thelabnyc/thelabui/src/utils/richText";

import iconCarouselArrowLeft from "../../../../img/icons/carousel-arrow-left.svg";
import iconCarouselArrowRight from "../../../../img/icons/carousel-arrow-right.svg";
import iconCircleClose from "../../../../img/icons/circle-close.svg";
import { Image } from "../../../components/Image";
import { RichText } from "../../../components/RichText";
import { ProductCompareTheme } from "../../../constants";
import { IProductID, ISafeHTML, WebPageURL } from "../../../models/nominals";
import {
    IAttributeOptionGroup,
    ICompareTile,
    IProductTileAttributeRow,
    ITileID,
} from "../../../models/product-compare";
import { BreakPoint, IViewport } from "../../../models/screen";
import { useStaticURL } from "../../../utils/urls";
import { SizeSelector } from "../ProductCompareSizeSelector";
import { ProductCompareTileAttributeRow } from "../ProductCompareTileAttributeRow";
import { ProductCompareTileCTA } from "../ProductCompareTileCTA";
import { ProductCompareTileHeader } from "../ProductCompareTileHeader";
import { ProductCompareAnalyticsProvider } from "../analytics";
import { useViewItemListTracking } from "../hooks/useViewItemListTracking";
import { getSelectedVariant } from "../selectors";
import { setShowStartingAt } from "../utility";

import styles from "./ProductCompareGridContent.module.scss";

function buildInitialAccordionRows(selectedTiles: readonly ICompareTile[]): {
    [id: number]: boolean;
} {
    const accordionRows: { [id: number]: boolean } = {};
    if (selectedTiles.length > 0) {
        selectedTiles[0].rows.forEach((row: IProductTileAttributeRow) => {
            accordionRows[row.row_type.id] = true;
        });
    }
    return accordionRows;
}

function getViewableTiles(
    selectedTiles: readonly ICompareTile[],
    currentPosition: number,
    tilesPerSlide: number,
): ICompareTile[] {
    const increment = currentPosition + tilesPerSlide;

    if (selectedTiles.length - currentPosition === 1) {
        return selectedTiles.slice(currentPosition - 1, increment);
    }
    return selectedTiles.slice(currentPosition, increment);
}

function getTilesPerSlide(viewportWidth: number): number {
    if (viewportWidth > BreakPoint.LARGE) {
        return 5;
    }
    return 2;
}

function updateURLWithTileIDs(tileIDs: readonly ITileID[]): void {
    const window = getWindow();
    if (!window) return;

    const urlParams = new URLSearchParams(window.location.search);
    if (tileIDs.length > 0) {
        urlParams.set("tiles", tileIDs.join(","));
    } else {
        urlParams.delete("tiles");
    }
    const newURL = `${window.location.origin}${window.location.pathname}?${urlParams.toString()}`;
    window.history.replaceState(window.history.state, "", newURL);
}

type HeaderContent = {
    header: ISafeHTML;
    linkHref: string | null;
};

function buildHeaderContent(
    header: ISafeHTML,
    returnUrl: string,
    selectedTileIDs: readonly ITileID[],
    isMinimal: boolean | undefined,
): HeaderContent {
    if (returnUrl && !isMinimal) {
        let href = returnUrl;
        if (selectedTileIDs.length > 0) {
            const separator = href.includes("?") ? "&" : "?";
            href = `${href}${separator}tiles=${encodeURIComponent(selectedTileIDs.join(","))}`;
        }
        return { header, linkHref: href };
    }
    return { header, linkHref: null };
}

function renderHeaderContent(content: HeaderContent): React.ReactNode {
    if (content.linkHref) {
        const headerElem = (
            <RichText key="header" html={content.header} inline={true} />
        );
        const linkElem = (
            <a
                key="link"
                className="al-compareresults-changeselection"
                href={content.linkHref}
            >
                {t`Add/Change Selection`}
            </a>
        );
        return (
            <>
                {headerElem}
                {linkElem}
            </>
        );
    }
    return <RichText html={content.header} />;
}

export const ProductCompareGrid = (
    props: Immutable<{
        header: ISafeHTML;
        tiles: readonly ICompareTile[];
        basketLink: WebPageURL;
        financingLink: WebPageURL;
        preselectedSize: string | null;
        sizeAttributeOptionGroup: IAttributeOptionGroup | null;
        priceSelectValue: string;
        pageRootProduct: IProductID | null;
        theme: ProductCompareTheme;
        returnUrl: string;
        viewport: IViewport;
        showFirstAvailablePrice?: boolean;
        showRating?: boolean;
        showStartingAt?: boolean;
        isMinimal?: boolean;
    }>,
): React.ReactNode => {
    const staticURL = useStaticURL();
    const [currentPosition, setCurrentPosition] = React.useState(0);
    const [selectedTileIDs, setSelectedTileIDs] = React.useState<
        readonly ITileID[]
    >(() => props.tiles.map((tile) => tile.product_compare_tile.id));
    const [selectedTiles, setSelectedTiles] = React.useState<
        readonly ICompareTile[]
    >(props.tiles);
    const [tilesPerSlide, setTilesPerSlide] = React.useState(
        getTilesPerSlide(props.viewport.width),
    );
    const [accordionRows, setAccordionRows] = React.useState<{
        [id: number]: boolean;
    }>(() => buildInitialAccordionRows(props.tiles));

    const compareTilesRef = React.useRef<HTMLDivElement>(null);
    const productHeaderRowRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const window = getWindow();
        if (!window) return;

        const handleResize = (): void => {
            setTilesPerSlide(getTilesPerSlide(props.viewport.width));
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => {
            if (window) {
                window.removeEventListener("resize", handleResize);
            }
        };
    }, [props.viewport.width]);

    // Setup IntersectionObserver for sticky header
    React.useEffect(() => {
        const target = compareTilesRef.current;
        const header = productHeaderRowRef.current;

        if (target && header) {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        header.setAttribute("aria-hidden", "true");
                    } else {
                        header.setAttribute("aria-hidden", "false");
                    }
                },
                {
                    threshold: 0,
                },
            );
            observer.observe(target);

            return () => {
                observer.disconnect();
            };
        }
    }, []);

    // Adjust position when tiles are removed from the end
    React.useEffect(() => {
        if (selectedTiles.length - currentPosition === 1) {
            setCurrentPosition(currentPosition - 1);
        }
    }, [selectedTiles.length, currentPosition]);

    const onAccordionTriggerClick = (
        event: React.MouseEvent<HTMLElement>,
        rowID: number,
    ): void => {
        event.preventDefault();
        setAccordionRows((prev) => ({
            ...prev,
            [rowID]: !prev[rowID],
        }));
    };

    const onRemoveButtonClick = (
        e: React.MouseEvent<HTMLElement>,
        tileID: ITileID,
    ): void => {
        e.preventDefault();

        const newTileIDs = selectedTileIDs.filter((id) => id !== tileID);
        const newTiles = selectedTiles.filter((tile) =>
            newTileIDs.includes(tile.product_compare_tile.id),
        );

        setSelectedTileIDs(newTileIDs);
        setSelectedTiles(newTiles);

        if (props.returnUrl) {
            updateURLWithTileIDs(newTileIDs);
        }
    };

    const onPrevSlideClick = (): void => {
        setCurrentPosition(currentPosition - 1);
    };

    const onNextSlideClick = (): void => {
        setCurrentPosition(currentPosition + 1);
    };

    const buildCompareTiles = (): React.JSX.Element[] => {
        const viewableTiles = getViewableTiles(
            selectedTiles,
            currentPosition,
            tilesPerSlide,
        );

        return viewableTiles.map((tile: ICompareTile) => {
            const variant = getSelectedVariant(tile, props.priceSelectValue);
            const showDescription =
                props.theme !== ProductCompareTheme.STEP_UP &&
                props.theme !== ProductCompareTheme.SELECT
                    ? false
                    : true;

            const tileClasses = classNames({
                tile: true,
                [styles.tile]: props.theme === ProductCompareTheme.DEFAULT,
                [styles.tileThemeSelect]:
                    props.theme === ProductCompareTheme.SELECT,
                [styles.tileThemeStepUp]:
                    props.theme === ProductCompareTheme.STEP_UP,
            });
            return (
                <div
                    aria-label="product"
                    role="group"
                    className={tileClasses}
                    key={`${tile.product_compare_tile.id}`}
                >
                    {props.returnUrl && !props.isMinimal && (
                        <button
                            type="button"
                            className={`${styles.xButton} al-compareresults-xoutproduct`}
                            onClick={(e) => {
                                onRemoveButtonClick(
                                    e,
                                    tile.product_compare_tile.id,
                                );
                            }}
                        >
                            <SVG
                                className="basket-savings__title-icon"
                                aria-hidden="true"
                                src={iconCircleClose}
                            />
                            <span className="ada-screenreader-only">
                                {t`Remove product from compare`}
                            </span>
                        </button>
                    )}
                    <ProductCompareTileHeader
                        data={tile}
                        selectedVariant={variant}
                        pageRootProduct={props.pageRootProduct}
                        theme={props.theme}
                        showDescription={showDescription}
                        showRating={
                            props.showRating !== undefined
                                ? props.showRating
                                : true
                        }
                    />
                    {props.theme !== ProductCompareTheme.STEP_UP &&
                        props.theme !== ProductCompareTheme.SELECT && (
                            <ProductCompareTileCTA
                                data={tile}
                                selectedVariant={variant}
                                financingLink={props.financingLink}
                                showStartingAt={
                                    props.showStartingAt
                                        ? props.showStartingAt
                                        : setShowStartingAt(
                                              tile,
                                              props.sizeAttributeOptionGroup,
                                          )
                                }
                                theme={props.theme}
                            />
                        )}
                </div>
            );
        });
    };

    const buildChildRow = (
        row: IProductTileAttributeRow,
    ): React.JSX.Element[] => {
        const viewableTiles = getViewableTiles(
            selectedTiles,
            currentPosition,
            tilesPerSlide,
        );
        return viewableTiles.map((tile: ICompareTile) => (
            <ProductCompareTileAttributeRow
                key={`${tile.product_compare_tile.id}-${row.row_type.id}`}
                row={row}
                data={tile}
                theme={props.theme}
                basketLink={props.basketLink}
                financingLink={props.financingLink}
                priceSelectValue={props.priceSelectValue}
                showStartingAt={props.showStartingAt}
                sizeAttributeOptionGroup={props.sizeAttributeOptionGroup}
            />
        ));
    };

    const buildChildRows = (
        rows: ReadonlyArray<IProductTileAttributeRow>,
    ): React.JSX.Element[] => {
        return rows.map((row: IProductTileAttributeRow) => {
            const attributeRowContainerClasses = classNames({
                [styles.childRow]: true,
                [styles.attributeRowContainer]: true,
                [styles.richtext]: row.row_type.row_type === "richtext",
                [styles.attributeGrid]:
                    row.row_type.row_type === "attribute-grid",
                [styles.attributeOptionList]:
                    row.row_type.row_type === "attribute-option-list",
                [styles.shopNow]: row.row_type.row_type === "shop-now",
                [styles.microconfigurator]:
                    row.row_type.row_type === "microconfigurator",
                [styles.productSelect]:
                    row.row_type.row_type === "product-select",
                [styles.upgrade]: row.row_type.row_type === "upgrade",
                [styles.dynamicPrice]:
                    row.row_type.row_type === "dynamic-price",
            });

            return (
                <div
                    className={attributeRowContainerClasses}
                    key={`${row.row_type.id}-child-row`}
                >
                    {buildChildRow(row)}
                </div>
            );
        });
    };

    const buildAttributeRow = (rowCounter: number): React.JSX.Element[] => {
        const viewableTiles = getViewableTiles(
            selectedTiles,
            currentPosition,
            tilesPerSlide,
        );
        return viewableTiles.map((tile: ICompareTile) => (
            <ProductCompareTileAttributeRow
                key={`${tile.product_compare_tile.id}-${tile.rows[rowCounter].row_type.id}`}
                row={tile.rows[rowCounter]}
                data={tile}
                theme={props.theme}
                basketLink={props.basketLink}
                financingLink={props.financingLink}
                priceSelectValue={props.priceSelectValue}
                showFirstAvailablePrice={props.showFirstAvailablePrice}
                showStartingAt={props.showStartingAt}
                sizeAttributeOptionGroup={props.sizeAttributeOptionGroup}
            />
        ));
    };

    const buildAttributeRows = (): React.JSX.Element[] => {
        return selectedTiles[0].rows.map((row: IProductTileAttributeRow) => {
            const rowCounter = selectedTiles[0].rows.indexOf(row);
            const attributeRowContainerClasses = classNames({
                [styles.attributeRowContainer]: true,
                [styles.attributeRowContainerThemeStepUp]:
                    props.theme === ProductCompareTheme.STEP_UP,
                [styles.richtext]: row.row_type.row_type === "richtext",
                [styles.attributeGrid]:
                    row.row_type.row_type === "attribute-grid",
                [styles.attributeOptionList]:
                    row.row_type.row_type === "attribute-option-list",
                [styles.shopNow]: row.row_type.row_type === "shop-now",
                [styles.microconfigurator]:
                    row.row_type.row_type === "microconfigurator",
                [styles.productSelect]:
                    row.row_type.row_type === "product-select",
                [styles.upgrade]: row.row_type.row_type === "upgrade",
                [styles.dynamicPrice]:
                    row.row_type.row_type === "dynamic-price",
            });
            const accordionButtonClasses = classNames({
                "accordion": true,
                "accordion--is-active": accordionRows[row.row_type.id],
                [styles.accordionRowTriggerButton]: true,
            });
            const accordionTargetClasses = classNames({
                "accordion-target": true,
                "accordion-target--is-active": accordionRows[row.row_type.id],
            });

            if (
                props.theme === ProductCompareTheme.STEP_UP ||
                props.theme === ProductCompareTheme.SELECT
            ) {
                return (
                    <div
                        key={row.row_type.id}
                        className={attributeRowContainerClasses}
                    >
                        <h4>{row.row_type.title}</h4>
                        <div>{buildAttributeRow(rowCounter)}</div>
                    </div>
                );
            }

            return (
                <div
                    key={row.row_type.id}
                    className={attributeRowContainerClasses}
                >
                    <button
                        className={accordionButtonClasses}
                        aria-expanded={accordionRows[row.row_type.id]}
                        onClick={(e) => {
                            onAccordionTriggerClick(e, row.row_type.id);
                        }}
                    >
                        <h4>{row.row_type.title}</h4>
                    </button>
                    <div
                        className={accordionTargetClasses}
                        aria-hidden={!accordionRows[row.row_type.id]}
                    >
                        {buildAttributeRow(rowCounter)}
                        {buildChildRows(row.children)}
                    </div>
                </div>
            );
        });
    };

    const buildStickyProductHeaderRow = (): React.JSX.Element[] => {
        const viewableTiles = getViewableTiles(
            selectedTiles,
            currentPosition,
            tilesPerSlide,
        );

        return viewableTiles.map((tile: ICompareTile) => {
            return (
                <div
                    className={styles.stickyHeaderTile}
                    key={`header-row-${tile.product_compare_tile.id}`}
                >
                    <RichText html={tile.product_compare_tile.title} />
                </div>
            );
        });
    };

    const buildShopMattressesLink = (): React.JSX.Element => {
        return (
            <a href={props.returnUrl} className="button">
                {t`Shop Mattresses`}
            </a>
        );
    };

    const renderSlideControls = (): React.JSX.Element | null => {
        if (props.viewport.width > BreakPoint.LARGE) {
            return null;
        }
        if (selectedTiles.length <= 2) {
            return null;
        }
        return (
            <nav className={styles.nav}>
                <button
                    aria-label={t`Previous`}
                    disabled={currentPosition === 0}
                    onClick={onPrevSlideClick}
                >
                    <SVG aria-hidden="true" src={iconCarouselArrowLeft} />
                </button>
                <button
                    aria-label={t`next`}
                    disabled={
                        currentPosition + tilesPerSlide >= selectedTiles.length
                    }
                    onClick={onNextSlideClick}
                >
                    <SVG aria-hidden="true" src={iconCarouselArrowRight} />
                </button>
            </nav>
        );
    };

    const rootClasses = classNames({
        [styles.root]: true,
        [styles.rootThemeCompareAndSelect]:
            props.theme === ProductCompareTheme.SELECT,
    });
    const headerClasses = classNames({
        [styles.header]: true,
        [styles.headerThemeStepUp]: props.theme === ProductCompareTheme.STEP_UP,
    });
    const productHeaderRowClasses = classNames({
        "product-header-row": true,
        [styles.productHeaderRow]: props.theme === ProductCompareTheme.DEFAULT,
        [styles.productHeaderRowThemeSelect]:
            props.theme === ProductCompareTheme.SELECT,
        [styles.productHeaderRowThemeStepUp]:
            props.theme === ProductCompareTheme.STEP_UP,
    });

    const controlsClasses = classNames({
        [styles.controls]: true,
        ["hidden"]: props.isMinimal,
    });
    const viewableTiles = getViewableTiles(
        selectedTiles,
        currentPosition,
        tilesPerSlide,
    );
    const { listMetadata, viewedProducts } = useViewItemListTracking(
        viewableTiles,
        props.priceSelectValue,
        props.header,
        "Product Compare Grid",
        selectedTileIDs.length,
    );

    if (selectedTiles.length > 0) {
        return (
            <ProductCompareAnalyticsProvider
                listMetadata={listMetadata}
                viewedProducts={viewedProducts}
            >
                <div className={rootClasses}>
                    <header className={headerClasses}>
                        {renderHeaderContent(
                            buildHeaderContent(
                                props.header,
                                props.returnUrl,
                                selectedTileIDs,
                                props.isMinimal,
                            ),
                        )}
                    </header>
                    {renderSlideControls()}
                    {props.theme !== ProductCompareTheme.STEP_UP &&
                        props.theme !== ProductCompareTheme.SELECT && (
                            <div className={controlsClasses}>
                                {props.sizeAttributeOptionGroup && (
                                    <SizeSelector
                                        sizeAttributeOptionGroup={
                                            props.sizeAttributeOptionGroup
                                        }
                                        preselectedSize={props.preselectedSize}
                                        showSizeSelector={!props.isMinimal}
                                    />
                                )}
                                <button
                                    onClick={() => {
                                        const window = getWindow();
                                        if (window) {
                                            window.print();
                                        }
                                    }}
                                >
                                    <span>{t`Print Comparison`}</span>
                                </button>
                            </div>
                        )}
                    <div
                        className={productHeaderRowClasses}
                        aria-hidden="true"
                        ref={productHeaderRowRef}
                    >
                        {buildStickyProductHeaderRow()}
                    </div>
                    <div className={styles.tiles} ref={compareTilesRef}>
                        {buildCompareTiles()}
                    </div>
                    {buildAttributeRows()}
                </div>
            </ProductCompareAnalyticsProvider>
        );
    } else {
        return (
            <div className={styles.root}>
                <header className={styles.header}>
                    {renderHeaderContent(
                        buildHeaderContent(
                            props.header,
                            props.returnUrl,
                            selectedTileIDs,
                            props.isMinimal,
                        ),
                    )}
                </header>
                <div className={styles.emptyState}>
                    <Image
                        src={`${staticURL}tsicommon_cms/img/empty-state.jpg`}
                        alt=""
                    />
                    <h3>
                        {richT(
                            t`You currently do not have<br/>any mattresses to compare`,
                        )}
                    </h3>
                    {buildShopMattressesLink()}
                </div>
            </div>
        );
    }
};
